import { useStore } from "vuex";
import { ProductIds, professionalGradFlowsDictionary } from "@/config/constants";
import { Flows } from "@/config/enums";
import { computed } from "vue";
import { AppData, Applicant, Application } from "@/models/application";
import { Programs, Products } from "@/config/enums";
import { LenderBranding } from "@/store/applicationsModule";
import { StudentVM, StudentDto } from "@/models/student";

export default function useApplicationData() {
  const store = useStore();

  const appData = computed<AppData>(() => {
    return store?.state?.appData;
  });

  const applicants = computed<Applicant[]>(() => store.state.appData?.applicants);

  const application = computed<Application>(() => store.state.appData?.application);

  const applicationId = computed<number>(() => store.state.appData?.application?.id);

  const borrowAmountModalAlertMessage = computed<string>(() => {
    return productTypeId.value === ProductIds.LOC
      ? "Please select your school, academic term, and anticipated graduation date first before you select your requested amount."
      : "School confirmation details must be completed prior to selecting Loan Terms.";
  });

  const defaultSupportEmail = computed<string>(() => "member_support@studentchoice.org");

  const defaultTollFreeNumber = computed<string>(() => "1-800-381-7058");

  const emailAddress = computed<string>(() => {
    return store?.state?.lenderData && store?.state?.lenderData?.supportEmailAddress
      ? store?.state?.lenderData?.supportEmailAddress
      : defaultSupportEmail.value;
  });

  const hasRepaymentBasedRates = computed<boolean>(
    () => store.state.loanConfigurationModule?.applicationInfo?.hasRepaymentBasedRates,
  );

  const hasResidency = computed<boolean>(
    () => store.state.loanConfigurationModule?.applicationInfo?.hasResidency,
  );

  const hrefTFN = computed<string>(() => {
    return tollFreeNumber.value.startsWith("1")
      ? `+${tollFreeNumber.value.replace(/-/g, "")}`
      : `+1${tollFreeNumber.value.replace(/-/g, "")}`;
  });

  const inlineProductDisplay = computed<string>(() => {
    if (productTypeId.value === Number(Products.LOC)) {
      return "line of credit";
    }
    return "loan";
  });

  const isClinicalPsychology = computed<boolean>(() => {
    const programTypeIds = [
      Number(Programs.CLINICAL_PSYCHOLOGY),
      Number(Programs.CLINICAL_PSYCHOLOGY2),
      Number(Programs.CLINICAL_PSYCHOLOGY3),
    ] as number[];

    return programTypeIds.includes(programTypeId.value);
  });

  const isCombineProfessionalGrad = computed<boolean>(() => {
    const programTypeIds = [
      Number(Programs.MEDICAL_AND_OSTEOPATHIC),
      Number(Programs.MEDICAL_AND_OSTEOPATHIC2),
      Number(Programs.MEDICAL_AND_OSTEOPATHIC3),
      Number(Programs.ADVANCED_DEGREE),
      Number(Programs.ADVANCED_DEGREE2),
      Number(Programs.ADVANCED_DEGREE3),
      Number(Programs.HEALTH_PROFESSIONS),
      Number(Programs.HEALTH_PROFESSIONS2),
      Number(Programs.HEALTH_PROFESSIONS3),
    ] as number[];

    return programTypeIds.includes(programTypeId.value);
  });

  const isDental = computed<boolean>(() => {
    const programTypeIds = [
      Number(Programs.DENTAL),
      Number(Programs.DENTAL2),
      Number(Programs.DENTAL3),
    ] as number[];

    return programTypeIds.includes(programTypeId.value);
  });

  const isDomesticGrad = computed<boolean>(() => {
    return professionalGradFlowsDictionary[Flows.STRICT].includes(programTypeId.value);
  });

  const isLaw = computed<boolean>(() => {
    const programTypeIds = [
      Number(Programs.LAW),
      Number(Programs.LAW2),
      Number(Programs.LAW3),
    ] as number[];

    return programTypeIds.includes(programTypeId.value);
  });

  const isMedical = computed<boolean>(() => {
    return professionalGradFlowsDictionary[Flows.MDDO].includes(programTypeId.value);
  });

  const isMedicalGrad = computed<boolean>(() => {
    return professionalGradFlowsDictionary[Flows.MEDICAL].includes(programTypeId.value);
  });

  const isOptometry = computed<boolean>(() => {
    const programTypeIds = [
      Number(Programs.OPTOMETRY),
      Number(Programs.OPTOMETRY2),
      Number(Programs.OPTOMETRY3),
    ] as number[];

    return programTypeIds.includes(programTypeId.value);
  });

  const isParentLOC = computed<boolean>(
    () => productTypeId.value === ProductIds.LOC && programTypeId.value === Number(Programs.PARENT),
  );

	const isPel = computed<boolean>(() => productTypeId.value === ProductIds.PEL);

  const isPharmacy = computed<boolean>(() => {
    const programTypeIds = [
      Number(Programs.PHARMACY),
      Number(Programs.PHARMACY2),
      Number(Programs.PHARMACY3),
    ] as number[];

    return programTypeIds.includes(programTypeId.value);
  });

  const isPodiatry = computed<boolean>(() => {
    const programTypeIds = [
      Number(Programs.PODIATRY),
      Number(Programs.PODIATRY2),
      Number(Programs.PODIATRY3),
    ] as number[];

    return programTypeIds.includes(programTypeId.value);
  });

  const isProfessionalGrad = computed<boolean>(() => {
    return professionalGradFlowsDictionary[Flows.PROF].includes(programTypeId.value);
  });

  const isTheology = computed<boolean>(() => {
    const programTypeIds = [
      Number(Programs.THEOLOGY),
      Number(Programs.THEOLOGY2),
      Number(Programs.THEOLOGY3),
    ] as number[];

    return programTypeIds.includes(programTypeId.value);
  });

  const isValidProfessionalGradFlow = computed<boolean>(() => {
    return (
      isMedical.value ||
      isProfessionalGrad.value ||
      isMedicalGrad.value ||
      isCombineProfessionalGrad.value
    );
  });

  const isVeterinary = computed<boolean>(() => {
    const programTypeIds = [
      Number(Programs.VETERINARY_MEDICINE),
      Number(Programs.VETERINARY_MEDICINE2),
      Number(Programs.VETERINARY_MEDICINE3),
    ] as number[];

    return programTypeIds.includes(programTypeId.value);
  });

  const isUGLOC = computed<boolean>(
    () => productTypeId.value == ProductIds.LOC && programTypeId.value == Programs.UNDERGRADUATE,
  );

  const lenderBranding = computed<LenderBranding | undefined>(
    () => store.getters["applicationsModule/Branding"],
  );

  const productTypeId = computed<number>(() => appData.value?.application.productTypeId || 0);

  const programTypeId = computed<number>(() => appData.value?.application.programTypeId || 0);
  
	const referenceId = computed<string>(() => store.state.appData?.application?.referenceId);

	const student = computed<StudentDto | null>(() => store.state.appData?.application?.student);

  const studentType = computed<string>(() => {
    if (
      programTypeId.value === Number(Programs.UNDERGRADUATE) ||
      programTypeId.value === Number(Programs.INTERNATIONAL_UNDERGRADUATE)
    ) {
      return "undergrad";
    } else if (isMedical.value) {
      return "medical";
    } else if (isDomesticGrad.value || isMedicalGrad.value || isProfessionalGrad.value) {
      if (isClinicalPsychology.value) {
        return "clinical psychology";
      } else if (isDental.value) {
        return "dental";
      } else if (isLaw.value) {
        return "law";
      } else if (isPharmacy.value) {
        return "pharmacy";
      } else if (isPodiatry.value) {
        return "podiatry";
      } else if (isOptometry.value) {
        return "optometry";
      } else if (isTheology.value) {
        return "theology";
      } else if (isVeterinary.value) {
        return "veterinary";
      } else {
        return "graduate";
      }
    } else {
      return "";
    }
  });

  const tollFreeNumber = computed<string>(() => {
    if (lenderBranding.value) {
      return lenderBranding.value.tollFreeNumber && lenderBranding.value.tollFreeNumber.length > 0 ? lenderBranding.value.tollFreeNumber : "N/A";
    }
    return store.state.lenderData && store.state.lenderData.tollFreeNumber
      ? store.state.lenderData.tollFreeNumber
      : defaultTollFreeNumber.value;
  });

  const useProfessionalGradLoanOptionsModal = computed<boolean>(() => {
    return isValidProfessionalGradFlow.value && hasRepaymentBasedRates.value;
  });

  return {
    appData,
    applicants,
    application,
    applicationId,
    borrowAmountModalAlertMessage,
    emailAddress,
    hasRepaymentBasedRates,
    hasResidency,
    hrefTFN,
    isUGLOC,
    inlineProductDisplay,
    isClinicalPsychology,
    isCombineProfessionalGrad,
    isDental,
    isDomesticGrad,
    isLaw,
    isMedical,
    isMedicalGrad,
    isOptometry,
		isParentLOC,
		isPel,
    isPharmacy,
    isPodiatry,
    isProfessionalGrad,
    isTheology,
    isValidProfessionalGradFlow,
    isVeterinary,
    programTypeId,
    productTypeId,
		referenceId,
		student,
    studentType,
    tollFreeNumber,
    useProfessionalGradLoanOptionsModal,
  };
}
